import { Link, Outlet, useLocation } from "react-router-dom";
import {
  MessageSquare,
  Workflow,
  BrainCircuit,
  MessageCircle,
  Phone,
  TrendingUp,
  Settings,
} from "lucide-react";
import { cn } from "../lib/utils";
import { auth } from "../lib/firebase";
import { signInWithPopup, signOut, type User } from "firebase/auth";
import { GoogleAuthProvider } from "firebase/auth";
import { useEffect, useState, useCallback } from "react";
import { WhatsAppConnectModal } from "./WhatsAppConnectModal";

const navigation = [
  { name: "Dashboard", href: "/", icon: MessageSquare },
  { name: "Conversion Flow", href: "/conversion-flow", icon: TrendingUp },
  { name: "Auto Follow-up", href: "/auto-follow-up", icon: Workflow },
  { name: "AI Knowledge Base", href: "/ai-knowledge-base", icon: BrainCircuit },
  { name: "AI Calling", href: "/calling", icon: Phone },
];

export function Layout() {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [status, setStatus] = useState<string>("disconnected");
  const [pairingCode, setPairingCode] = useState("");
  const [qrCodeStr, setQrCodeStr] = useState("");
  const [loading, setLoading] = useState(false);
  const [localOwnerId, setLocalOwnerId] = useState<string>("");

  useEffect(() => {
    return auth.onAuthStateChanged((u) => setUser(u));
  }, []);

  // Fetch the server-stored ownerId so connecting works without Google sign-in
  useEffect(() => {
    fetch("/api/whatsapp/owner")
      .then((r) => r.json())
      .then((d) => { if (d.ownerId) setLocalOwnerId(d.ownerId); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkStatus() {
      try {
        const res = await fetch("/api/whatsapp/status");
        if (!cancelled && res.ok) {
          const data = await res.json();
          setStatus(data.status);
          setQrCodeStr(data.qr || "");
        }
      } catch {
        // Server may be restarting, will retry on next interval
      }
    }

    checkStatus(); // immediate check on mount

    const interval = setInterval(checkStatus, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showProfileMenu) setShowProfileMenu(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showProfileMenu]);

  const handleLogin = useCallback(() => {
    signInWithPopup(auth, new GoogleAuthProvider()).catch(console.error);
  }, []);

  const handleLogout = useCallback(() => {
    signOut(auth).catch(console.error);
    setShowProfileMenu(false);
  }, []);

  const handleConnect = useCallback(
    async (phoneNumber: string, useQr: boolean) => {
      setLoading(true);
      setPairingCode("");
      setQrCodeStr("");
      try {
        const res = await fetch("/api/whatsapp/connect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phoneNumber.replace(/\D/g, ""),
            ownerId: localOwnerId || auth.currentUser?.uid || "local-user",
            useQr,
          }),
        });
        const data = await res.json();
        if (data.code) setPairingCode(data.code);
      } catch (e) {
        console.error("Connection error:", e);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleDisconnect = useCallback(async () => {
    setLoading(true);
    try {
      await fetch("/api/whatsapp/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setStatus("disconnected");
    } catch (e) {
      console.error("Disconnect error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const userInitial =
    user?.displayName?.charAt(0) || user?.email?.charAt(0) || "?";

  return (
    <div className="flex h-screen w-full bg-slate-50 text-slate-900 font-sans">
      <WhatsAppConnectModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        status={status}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        loading={loading}
        pairingCode={pairingCode}
        qrCodeStr={qrCodeStr}
      />

      <aside className="w-64 flex flex-col border-r bg-white shrink-0">
        <div className="p-4 flex items-center gap-2 border-b">
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight">Kraya</span>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-green-50 text-green-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <item.icon
                  className={cn(
                    "w-5 h-5",
                    isActive ? "text-green-600" : "text-slate-400"
                  )}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t">
          <button
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all w-full"
            onClick={() => {}}
          >
            <Settings className="w-5 h-5 text-slate-400" />
            Settings
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b bg-white flex items-center justify-between px-6 shrink-0">
          <h1 className="text-sm font-medium">
            {user ? (
              <>
                Welcome,{" "}
                <span className="font-semibold">{user.displayName}</span>
              </>
            ) : (
              "Welcome to Kraya"
            )}
          </h1>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowConnectModal(true)}
              className={cn(
                "px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all",
                status === "connected"
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-green-600 hover:bg-green-700 text-white shadow-sm"
              )}
            >
              <MessageCircle className="w-4 h-4" />
              {status === "connected"
                ? "WhatsApp Connected"
                : "Connect WhatsApp"}
            </button>

            {user ? (
              <div className="relative">
                <button
                  className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold overflow-hidden hover:bg-indigo-200 transition-colors border-2 border-transparent hover:border-indigo-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowProfileMenu((v) => !v);
                  }}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    userInitial
                  )}
                </button>

                {showProfileMenu && (
                  <div
                    className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border z-50 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-4 border-b">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        {user.displayName || "User"}
                      </p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {user.email || ""}
                      </p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-colors"
              >
                Sign in with Google
              </button>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-hidden relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
